import {
  PEST_PORTAL,
  type PestTone,
  type ResidentPestKey,
  resolvePestStatusText,
} from '@ps/common-features/helpers/pestCoverage'
import { Logger } from '@ps/common-features/sentry/loggerService'
import type { TrackingPest } from 'src/api/domains/service-tracking/serviceTracking.types'

import { TreatmentStatus } from '../constants/treatment'

/** The shared status keys double as this app's i18n keys, so the two stay in step. */
const TREATMENT_STATUS: Record<ResidentPestKey, TreatmentStatus> = {
  approved: TreatmentStatus.Approved,
  covered: TreatmentStatus.Covered,
  declined: TreatmentStatus.Declined,
  needsReview: TreatmentStatus.NeedsReview,
  notApproved: TreatmentStatus.NotApproved,
  notCovered: TreatmentStatus.NotCovered,
  paid: TreatmentStatus.Paid,
  paymentRequired: TreatmentStatus.PaymentRequired,
  propertyManagerReview: TreatmentStatus.PropertyManagerReview,
  quoteAtTimeOfService: TreatmentStatus.QuoteAtTimeOfService,
}

/** Nothing to show: either the row hides the pest, or its status was not recognised. */
const NOTHING_TO_SHOW = { status: TreatmentStatus.Hidden, tone: null } as const

export type ResolvedTreatmentStatus = {
  status: TreatmentStatus
  /** `null` when nothing is shown. */
  tone: PestTone | null
}

/**
 * The resident-facing status for a pest, from its status on the request and, when there
 * is one, on the work order.
 *
 * The mapping itself lives in `@ps/common-features`, shared with the property manager
 * portal. Only the wording is local, in this app's locale files.
 */
export const resolveTreatmentStatus = (
  requestPestStatusSlug: TrackingPest['status']['slug'],
  workOrderPestStatusSlug?: TrackingPest['status']['slug'],
  logContext?: {
    requestNumber?: number
    workOrderId?: number
    providerName?: string
  },
): ResolvedTreatmentStatus => {
  const text = resolvePestStatusText(
    PEST_PORTAL.RESIDENT,
    requestPestStatusSlug,
    workOrderPestStatusSlug,
  )

  if (text === undefined) {
    Logger.error('Invalid pest status: unknown request pest status', {
      context: {
        ...logContext,
        requestPestStatusSlug,
        workOrderPestStatusSlug,
      },
    })
    return NOTHING_TO_SHOW
  }

  if (text === null) {
    return NOTHING_TO_SHOW
  }

  return { status: TREATMENT_STATUS[text.key], tone: text.tone }
}
